// 0026: Generic category-tree isolation layer.
//
// The storefront data is TODAY a fixed 2-level hierarchy made of two different entity
// types: Category (level 1, image = imageURL) -> ItemGroup (level 2, image = pictureUrl).
// There is no self-referencing tree and the client payload is not recursive.
//
// The whole gallery (traversal + breadcrumb + tiles) goes through the accessors below
// and NEVER reads .itemGroups / .categoryName / .pictureUrl directly. That is the
// genericity guarantee: when the model later becomes a real deep tree (ParentCategoryId
// + recursive Children), ONLY this module changes — the components keep working over
// `getChildren` / `isLeaf` unchanged.

// Root nodes = the parent categories already loaded into Redux by AppBootstrap.
// `categories` is the redux value `state.categoryReducer.categories` => { items: [...] }.
export const getRootNodes = (categories) => categories?.items ?? [];

// Children of a node. A Category exposes its ItemGroups; an ItemGroup is always a leaf.
// (When a deep tree arrives, return node.children here and nothing else changes.)
export const getChildren = (node) => node?.itemGroups ?? [];

// A node is a leaf iff it has no children — works at any depth.
export const isLeaf = (node) => getChildren(node).length === 0;

export const getNodeId = (node) => node?.id;

// Name accessor tolerant of either entity shape.
export const getNodeName = (node) => node?.categoryName ?? node?.itemGroupName ?? "";

// Image accessor: Category -> imageURL, ItemGroup -> pictureUrl. Empty string when none.
export const getNodeImage = (node) => node?.imageURL ?? node?.pictureUrl ?? "";

// Find a root category by id (used by the /gallery/:categoryId drill view).
export const findCategoryById = (categories, id) =>
  getRootNodes(categories).find((node) => getNodeId(node) === id) ?? null;

// Resolve the EXISTING product-list route a leaf click must reuse (unchanged routes).
// - An ItemGroup leaf (it carries a parent categoryId) -> the group product route.
// - A leaf Category (no item groups) -> the category product route.
// `parentCategory` is the category we are currently drilled into (may be null at root).
export const resolveLeafRoute = (node, parentCategory = null) => {
  // ItemGroup: prefer its own categoryId, fall back to the category we drilled from.
  const parentCategoryId = node?.categoryId ?? getNodeId(parentCategory);
  const isItemGroup = parentCategoryId != null && node?.itemGroups === undefined;

  if (isItemGroup) {
    return `/categories/${parentCategoryId}/itemGroup/${getNodeId(node)}`;
  }

  // Leaf Category -> existing category product list.
  return `/categories/${getNodeId(node)}`;
};

// Where a tile click should go: drill deeper if the node has children, otherwise the
// existing product route. Shared by the gallery page AND the home category grid so the
// leaf-vs-children decision lives in exactly one place.
// Returns a route string for navigate().
export const resolveTileRoute = (node, parentCategory = null) => {
  if (isLeaf(node)) return resolveLeafRoute(node, parentCategory);
  return `/gallery/${getNodeId(node)}`;
};
